// SUB-OFFICE 7 — the canonical palette (art spec hexes; these supersede every
// older CSS value). Numbers, not THREE.Color, so any module may import freely.

export const PAL = {
  bone: 0xe9e4d8, // FORM BONE — walls, floors, benches, ceiling grid
  paper: 0xf4f1e6, // TRACTOR-FEED WHITE — live paper only
  carbon: 0x1c1a17, // CARBON RIBBON — ink, ropes, mechanism iron, tubes
  manila: 0xcba35c, // MANILA — desks, folders, drawer fronts
  gas: 0xedf4dc, // FLUORESCENT GAS — tube emissives + painted pools
  stampRed: 0xa3271e, // STAMP RED — consequence and nothing else
  clemency: 0x2e4c7e, // CLEMENCY BLUE — rarest ink; frost edge tint
  amber: 0xd99321, // VAULT AMBER — the color of the Machine's voice
  phosphor: 0x43e86c, // PHOSPHOR LEAK — Dept-6 door crack ONLY, nowhere else
  ghost: 0xb7b3a8, // GHOST GREY — ghosts, dust covers, probation target
  brass: 0x9c7e3c, // BRASS — levers, plaques, fixture rings
  gold: 0xc9a227, // GOLD FOIL — Beloved trim, certificates
  fog: 0xddd8cb,
  fogAfterHours: 0x8a8578,
  hemiSky: 0xe9e4d8,
  hemiGround: 0xb8ae96,
  keyLight: 0xfff6e4,
  wood: 0x6b4a2f, // Appeals office paneling (derived warm brown, in-gamut)
} as const;

/** css hex string for canvas work. */
export function css(c: number): string {
  return '#' + c.toString(16).padStart(6, '0');
}

/** lighten/darken a hex toward white(t>0)/black(t<0), t in [-1,1]. */
export function shade(c: number, t: number): number {
  const r = (c >> 16) & 255;
  const g = (c >> 8) & 255;
  const b = c & 255;
  const to = t > 0 ? 255 : 0;
  const k = Math.abs(t);
  const m = (v: number) => Math.round(v + (to - v) * k);
  return (m(r) << 16) | (m(g) << 8) | m(b);
}

/** linear mix of two hexes, t in [0,1] (0 = a, 1 = b). */
export function mix(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  const m = (x: number, y: number) => Math.round(x + (y - x) * t);
  return (m(ar, br) << 16) | (m(ag, bg) << 8) | m(ab, bb);
}

/** rgba() string from a hex + alpha, for canvas fills (colored AO/shadows). */
export function rgba(c: number, a: number): string {
  const r = (c >> 16) & 255, g = (c >> 8) & 255, b = c & 255;
  return `rgba(${r},${g},${b},${a})`;
}

// ---------------------------------------------------------------------------
// ART-DIRECTION v3 (§3.2) — PER-ROOM COLOR IDENTITY.
// Each room owns ONE key wash (floor pools + floor light tint) and ONE shadow
// hue (AO + vertex shadow darken TOWARD this, never neutral black). This is the
// flagship "colored shadows" move: a flat-shaded room looks crafted-expensive
// when its shadow carries the room's temperature instead of muddy black. The
// Recess Room (and its corridor) is the deliberate NEUTRAL exception — the one
// room with no colored soul, which IS its identity. Keys are RoomDef.id.
// ---------------------------------------------------------------------------

/** SHADOW HUE — what a room's AO, contact-shadow, and vertex-dim darken toward. */
export const ROOM_SHADOW: Record<string, number> = {
  intake: 0x23302e, // cold slate-teal (clinical)
  hall: 0x2a2620, // warm carbon-brown (window row / writing floor)
  gallery: 0x2e2415, // deep umber (reverent-warm)
  archive: 0x232a2e, // cool blue-slate (cold-orderly)
  rotunda: 0x2a2620, // warm — the Vault corner amber is a separate wash
  registry: 0x1e2436, // clemency-blue-black (mournful)
  appealsCorr: 0x2a1a0e, // warm brown-black (the only warm interior)
  appealsOffice: 0x2a1a0e,
  recessCorr: 0x22211e, // NEUTRAL grey-black — the deliberate exception
  recess: 0x22211e, // NEUTRAL grey-black — the deliberate exception
  // ---- M-C service ring --------------------------------------------------
  mailRoom: 0x232a2e, // cool utility -> blue-slate
  inspectionRow: 0x23302e, // cold interrogation -> slate-teal
  descentStair: 0x0c130e, // near-black carbon (Machine-Floor inversion preview)
  spineN: 0x1e2330, // cool service dark
  lostFound: 0x22211e, // dead-storage neutral
  engraving: 0x2a2620, // warm brown (brass + metal work)
  retention: 0x232a2e, // cool drawer canyon
};

/** KEY WASH — the tint of a room's floor pools and floor wash (its temperature). */
export const ROOM_WASH: Record<string, number> = {
  intake: 0xe4ecdf, // cool fluorescent (gas pushed cold)
  hall: 0xedf4dc, // neutral bone, pools in gas
  gallery: 0xe7d6ae, // warm brass-lit
  archive: 0xdde2e2, // cool steel-bone
  rotunda: 0xf4f1e6, // bright paper
  registry: 0xc9c6bc, // dim cool bone
  appealsCorr: 0xe6c88a, // tungsten
  appealsOffice: 0xe6c88a,
  recessCorr: 0xb7b3a8, // flat ghost — no pools authored
  recess: 0xb7b3a8, // flat ghost — no pools authored
  // ---- M-C service ring --------------------------------------------------
  mailRoom: 0xdde2e2, // cool utility
  inspectionRow: 0xe4ecdf, // cold interrogation white
  descentStair: 0xdde2e2, // cold bone at the walls; phosphor rises separately
  spineN: 0xdde2e2, // cool service light
  lostFound: 0xc9c6bc, // dim dead-storage
  engraving: 0xe6c88a, // warm work light
  retention: 0xdde2e2, // cool drawer canyon
};

/** SHADOW HUE for a room id, defaulting to the neutral (Recess) exception. */
export function roomShadow(id: string): number {
  return ROOM_SHADOW[id] ?? 0x22211e;
}

/** KEY WASH for a room id, defaulting to fluorescent gas. */
export function roomWash(id: string): number {
  return ROOM_WASH[id] ?? PAL.gas;
}
